import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedInboxPlate from '@paradigm/notifications/InboxPlate';
import { useNotifications } from '../../contexts/NotificationContext';
import { useGroup } from '../../contexts/GroupContext';
import { openNotification } from '../../utils/openNotification';
import { NUCLEUS_URL } from '../../services/apiConfig';
import api from '../../services/api';
import './DevInbox.css';

/**
 * The developer's inbox, at the right end of the location line.
 *
 * Kit 0.317 stopped drawing the dock inbox, and its InboxSeat only lists rows and
 * marks them read. The inbox this portal had answers a queue row where it stands
 * (Accept / Decline), keeps "Needs you" apart from "Happened", lets a feed row be
 * dismissed, and offers the desktop-notification ask. That plate is the shared one
 * (@paradigm/notifications/InboxPlate) — the same behaviour as Nucleus by
 * construction — so this file is only the seat and the wiring.
 *
 * The plate portals itself to <body>; DevInbox.css hangs it under this seat, above
 * the kit's scrim tier (940), with no scrim of its own.
 */

// What the shared actions open (a consent screen, a share picker) lives on Nucleus,
// not here: answering an invitation means choosing what you share with the group.
const NUCLEUS_PATHS = /^\/(grant|consent|manage|group\/)/;

export function goTo(to, { navigate, switchToGroup, switchToPersonal }) {
  const url = to?.url;
  if (!url) return;
  if (/^https?:\/\//.test(url)) { window.location.assign(url); return; }
  if (NUCLEUS_PATHS.test(url)) { window.location.assign(`${NUCLEUS_URL}${url}`); return; }
  openNotification(to, { navigate, switchToGroup, switchToPersonal });
}

function TrayGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

export default function DevInbox() {
  const notifications = useNotifications();
  const { activeGroup, switchToGroup, switchToPersonal } = useGroup();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const seatRef = useRef(null);
  const { unreadCount } = notifications;

  // Hang the plate from the seat: its top under the seat's foot, its right edge on
  // the seat's right edge. The plate is position: fixed and the seat is in the page's
  // flow, so measuring once on open left the plate behind when the window was resized
  // or the page scrolled under it. While it is up it is re-measured on resize, on any
  // scroll (capture: a scrolling column, not only the window), and when the seat or
  // the line holding it changes size (the line reflowing moves the seat).
  useLayoutEffect(() => {
    const seat = seatRef.current;
    if (!open || !seat) return undefined;
    const place = () => {
      const r = seat.getBoundingClientRect();
      const root = document.documentElement.style;
      root.setProperty('--dev-inbox-top', `${Math.round(r.bottom + 8)}px`);
      root.setProperty('--dev-inbox-right', `${Math.max(8, Math.round(window.innerWidth - r.right))}px`);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { capture: true, passive: true });
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(place);
      observer.observe(seat);
      if (seat.parentElement) observer.observe(seat.parentElement);
    }
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, { capture: true });
      if (observer) observer.disconnect();
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);
  const onNavigate = useCallback(
    (to) => goTo(to, { navigate, switchToGroup, switchToPersonal }),
    [navigate, switchToGroup, switchToPersonal],
  );

  return (
    <>
      <button
        ref={seatRef}
        type="button"
        // data-seat="inbox" is the plate's own-toggle contract: it lets this press
        // through its outside-press dismissal, so a re-press closes it once.
        data-seat="inbox"
        className="ofself-nav-toggle dev-inbox__seat"
        aria-expanded={open}
        aria-label={unreadCount > 0 ? `Inbox — ${unreadCount} unread` : 'Inbox'}
        title="Inbox"
        onClick={() => setOpen((v) => !v)}
      >
        <TrayGlyph />
        {unreadCount > 0 && <span className="dev-inbox__dot" aria-hidden="true" />}
      </button>
      {open && (
        <SharedInboxPlate
          surface="developer"
          spaceLabel={activeGroup ? activeGroup.name : 'Personal'}
          onClose={close}
          // No archive here: the portal has no settings window, so the plate is
          // the whole inbox. "Everything" just closes it.
          onOpenArchive={close}
          onNavigate={onNavigate}
          request={(path, options) => api.request(path, options)}
          {...notifications}
        />
      )}
    </>
  );
}
